import React from "react";

import { ClassificationPrompt } from "components/File-repository/Table/ClassificationPrompt";
import { EffectiveDatePrompt } from "components/File-repository/Table/EffectiveDatePrompt";
import { useFormatDateHook, usePrompt } from "hooks";
import { useInvestorsSelector, useTaskDealsSelect } from "store";
import { DealBase } from "store/types";

import { DealsMultiPrompt } from "./DealsMultiPrompt";
import { FileValues } from "./FilesUpload";
import { InvestorSelectPrompt } from "./InvestorSelectPrompt";

type InvestorOption = { id: number; name: string };

interface UseFilesTableRowParams {
  fileValue: FileValues;
  index: number;
  setFilesValues: React.Dispatch<React.SetStateAction<FileValues[]>>;
  updateDocumentType: (index: number, value: string | null) => void;
  updateEffectiveDate?: (index: number, value: string | null) => void;
  onDealChange?: (index: number, deals: DealBase[]) => void;
  dealInvestors: InvestorOption[];
  dealInvestorsLoading: boolean;
  dealInvestorsMap?: Record<number, InvestorOption[]>;
  isCloudMirror?: boolean;
}

export interface FilesTableRowData {
  file: File;
  document_type: string | null;
  document_type_ai_suggested?: boolean;
  investor_id: number | null;
  dealLabel: string;
  dealTooltip?: string;
  investorName: string;
  effectiveDateLabel: string;
  investorDisabled: boolean;
  openDealPrompt: (e: React.MouseEvent<HTMLDivElement>) => Promise<void>;
  openClassificationPrompt: (e: React.MouseEvent<HTMLDivElement>) => Promise<void>;
  openInvestorPrompt: (e: React.MouseEvent<HTMLDivElement>) => Promise<void>;
  openEffectiveDatePrompt: (e: React.MouseEvent<HTMLDivElement>) => Promise<void>;
}

// Builds the derived values and prompt handlers for a single FilesTable row, keeping the data-update
// logic out of the rendering component. Called once per row from FilesTableRow.
export const useFilesTableRowHook = ({
  fileValue,
  index,
  setFilesValues,
  updateDocumentType,
  updateEffectiveDate,
  onDealChange,
  dealInvestors,
  dealInvestorsLoading,
  dealInvestorsMap,
  isCloudMirror
}: UseFilesTableRowParams): FilesTableRowData => {
  const deals = useTaskDealsSelect();
  const allInvestors = useInvestorsSelector();
  const openPrompt = usePrompt();
  const formatDate = useFormatDateHook();

  const { file, document_type, investor_id, document_type_ai_suggested } = fileValue;
  const dealIds = fileValue.deal_ids ?? [];
  // Investors come from the primary (first) selected deal.
  const primaryDealId = dealIds[0] ?? null;
  const rowInvestors =
    isCloudMirror && primaryDealId && dealInvestorsMap ? dealInvestorsMap[primaryDealId] || [] : dealInvestors;

  const rowInvestorsLoading =
    isCloudMirror && primaryDealId && dealInvestorsMap ? !dealInvestorsMap[primaryDealId] : dealInvestorsLoading;

  const dealTitles = dealIds.map((id) => deals.find((d) => d.id === id)?.title).filter(Boolean) as string[];

  const isMultipleDeals = dealIds.length > 1;
  // When several deals are selected, show a compact "Multiple" label and reveal the full list on hover.
  const dealLabel = isMultipleDeals ? "Multiple" : dealTitles.join(", ");
  const dealTooltip = isMultipleDeals ? dealTitles.join(", ") : undefined;

  const investorOptions = isMultipleDeals ? [] : rowInvestors;
  const investorName = investor_id != null ? (allInvestors.find((inv) => inv.id === investor_id)?.name ?? "") : "";

  const effectiveDateLabel = fileValue.effective_date
    ? formatDate({ date: fileValue.effective_date, format: "DD/MM/YYYY" })
    : "";

  const investorDisabled = Boolean(isCloudMirror && !dealIds.length);

  const openDealPrompt = async (e: React.MouseEvent<HTMLDivElement>) => {
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: DealsMultiPrompt,
      props: { currentDealIds: dealIds }
    });
    if (!result) return;
    onDealChange?.(index, result.deals);
  };

  const openClassificationPrompt = async (e: React.MouseEvent<HTMLDivElement>) => {
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: ClassificationPrompt,
      props: { currentClassification: document_type ?? null }
    });
    if (!result) return;
    updateDocumentType(index, result.classification);
  };

  const openInvestorPrompt = async (e: React.MouseEvent<HTMLDivElement>) => {
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: InvestorSelectPrompt,
      props: {
        currentInvestorId: investor_id ?? null,
        dealInvestors: investorOptions,
        loading: isMultipleDeals ? false : rowInvestorsLoading
      }
    });
    if (!result) return;
    setFilesValues((prevState) =>
      prevState.map((item, i) => (i === index ? { ...item, investor_id: result.investor?.id ?? null } : item))
    );
  };

  const openEffectiveDatePrompt = async (e: React.MouseEvent<HTMLDivElement>) => {
    const result = await openPrompt({
      componentType: "popover",
      anchorEl: e.currentTarget as HTMLElement,
      Component: EffectiveDatePrompt,
      props: { currentDate: fileValue.effective_date ?? null }
    });
    if (!result) return;
    updateEffectiveDate?.(index, result.effectiveDate);
  };

  return {
    file,
    document_type,
    document_type_ai_suggested,
    investor_id,
    dealLabel,
    dealTooltip,
    investorName,
    effectiveDateLabel,
    investorDisabled,
    openDealPrompt,
    openClassificationPrompt,
    openInvestorPrompt,
    openEffectiveDatePrompt
  };
};
